import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useShelterAnimals, useShelters, useAnimalCoverUrls, ANIMAL_STATUS_LABELS } from '@rehome/shared';
import { Filter, Search, Loader2, AlertCircle } from 'lucide-react';

// ─── AnimalListPage ────────────────────────────────────────────────────────
// Admin animal list — fetches from Supabase, filtered by current shelter
// ===========================================================================

const AnimalListPage: React.FC = () => {
  const navigate = useNavigate();
  const { shelterId } = useParams<{ shelterId: string }>();
  const { data: shelterAnimals, loading, error, refetch } = useShelterAnimals(shelterId);
  const [filterSpecies, setFilterSpecies] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Apply client-side filters
  const filtered = useMemo(() => {
    return shelterAnimals.filter(a => {
      const matchSpecies = filterSpecies === 'all' || a.species === filterSpecies;
      const matchStatus = statusFilter === 'all' || a.status === statusFilter;
      const matchSearch = !searchTerm ||
        a.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (a.breed || '').toLowerCase().includes(searchTerm.toLowerCase());
      return matchSpecies && matchStatus && matchSearch;
    });
  }, [shelterAnimals, filterSpecies, statusFilter, searchTerm]);

  // A privát képek aláírt linket igényelnek — a szűrt listára EGY kötegelt
  // kérésben, nem kártyánként.
  const coverUrls = useAnimalCoverUrls(filtered);

  // A feliratok a közös szótárból jönnek (ANIMAL_STATUS_LABELS) — korábban
  // oldalanként újra le voltak írva, és egy új státusz csak az egyik helyen
  // jelent volna meg.
  const statusLabels = ANIMAL_STATUS_LABELS;
  const statusColors: Record<string, string> = {
    Available: 'bg-green-100 text-green-800', AdoptionPending: 'bg-amber-100 text-amber-800',
    Adopted: 'bg-blue-100 text-blue-800',
    Foster: 'bg-orange-100 text-orange-800', Medical: 'bg-red-100 text-red-800',
    StrayHold: 'bg-purple-100 text-purple-800', Quarantine: 'bg-yellow-100 text-yellow-800',
  };

  const statusBadgeStyles: Record<string, string> = {
    Available: 'bg-green-500', AdoptionPending: 'bg-amber-500', Medical: 'bg-red-500',
    Adopted: 'bg-blue-500',
    StrayHold: 'bg-purple-500', Quarantine: 'bg-yellow-500', Foster: 'bg-orange-500',
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Állatok Listája</h1>
          <p className="text-sm text-gray-500 mt-1">
            {loading ? 'Betöltés...' : `${filtered.length} állat (összesen ${shelterAnimals.length} a menhelyen)`}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input type="text" placeholder="Keresés név vagy fajta alapján..." className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none w-full md:w-64 bg-white" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <select className="pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none bg-white" value={filterSpecies} onChange={e => setFilterSpecies(e.target.value)}>
              <option value="all">Minden faj</option><option value="Kutya">Kutya</option><option value="Macska">Macska</option><option value="Egyéb">Egyéb</option>
            </select>
          </div>
          <div className="relative">
            <select className="pl-4 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none bg-white" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="all">Minden státusz</option>
              {Object.entries(statusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="text-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-gray-500">Állatok betöltése...</p>
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className="text-center py-16">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">Hiba történt az adatok betöltésekor.</p>
          <p className="text-sm text-gray-400 mb-4">{error}</p>
          <button onClick={refetch} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700">Újrapróbálás</button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && filtered.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <Search className="w-12 h-12 mx-auto mb-4 opacity-30" />
          {shelterAnimals.length === 0
            ? <p>Ehhez a menhelyhez még nincsenek állatok rögzítve. Használd az "Állat felvétele" menüpontot!</p>
            : <p>Nincs találat a keresési feltételekre.</p>}
        </div>
      )}

      {/* Animal grid */}
      {!loading && filtered.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filtered.map(animal => (
            <div key={animal.id} onClick={() => navigate(`/shelter/${shelterId}/animals/${animal.id}`)} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow cursor-pointer group">
              <div className="relative h-48 bg-gray-200 overflow-hidden">
                {coverUrls[animal.id] ? (
                  <img src={coverUrls[animal.id]} alt={animal.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">Nincs kép</div>
                )}
                <div className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-bold text-white ${statusBadgeStyles[animal.status] || 'bg-gray-500'}`}>
                  {statusLabels[animal.status] || animal.status}
                </div>
              </div>
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div><h3 className="text-lg font-bold text-gray-800">{animal.name}</h3><p className="text-sm text-gray-500">{animal.breed || 'Ismeretlen fajta'}</p></div>
                  <span className="text-sm font-medium bg-gray-100 px-2 py-1 rounded text-gray-600">{animal.age ? `${animal.age} év` : '?'}</span>
                </div>
                <div className="flex flex-wrap gap-1 mb-3">
                  {(animal.traits || []).slice(0, 3).map((t, i) => <span key={i} className="text-xs bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full border border-teal-100">{t}</span>)}
                </div>
                <p className="text-sm text-gray-600 line-clamp-2 mb-4 h-10">{animal.bio || 'Nincs leírás.'}</p>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${statusColors[animal.status] || 'bg-gray-100'}`}>{statusLabels[animal.status] || animal.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AnimalListPage;
